import { runInAction } from 'mobx';
import { WithdrawPayload } from '@monorepo/client-types';
import { MarketStatsFilterParams } from '@monorepo/community-stats-common';
import {
  AccountBalance,
  AccountsForRunningContestsResponse,
  AccountStats,
  AddCouponByCodeDto,
  AddItemByAccountDto,
  AddItemByCouponDto,
  AddItemToCartDto,
  AffiliateFaq,
  AffiliationCommission,
  AggregationRequest,
  AirwallexBeneficiaryFormOptions,
  Application,
  CartStep,
  ChangeCurrencyForCartDto,
  ClientApplicationConfig,
  ClosedContestWithWinners,
  ContestInformation,
  ContractData,
  ContractType,
  CreateAffiliateRequestType,
  OptionalAssets,
  PaginationRequest,
  PayoutSource,
  ProgressStep,
  RemoveDiscountForCartDto,
  RemoveItemFromCartDto,
  SubmitCartDto,
  TradingSystem,
  UpdateDiscountForCartDto,
  UpdateTradingSystemDto,
  UseCreditsOnCartDto,
  UserApproval,
  UserContestRanking,
} from '@monorepo/types';
import { getEnv, getRoot } from '@hub/helpers/getters.store';

export const getApiFactory = () => {
  return getEnv().apiFactory;
};

// APPLICATION CONFIG QUERIES

export const fetchApplicationConfig =
  async (): Promise<ClientApplicationConfig> => {
    const result = await getApiFactory().applicationConfigService.getAll();
    return result as unknown as ClientApplicationConfig;
  };

// ORDER QUERIES

export const fetchPurchases = (paginateRequest: PaginationRequest) => {
  return getApiFactory().purchaseService.getPurchases(paginateRequest);
};

export const fetchPurchaseInvoice = (purchaseId: string) => {
  return getApiFactory().purchaseService.getPurchaseInvoice(purchaseId);
};

export const fetchPurchaseStatus = (snapshotId: string) => {
  return getApiFactory().purchaseService.getPurchaseStatus(snapshotId);
};

export const fetchPurchaseMetrics = () => {
  return getApiFactory().purchaseService.getPurchaseMetrics();
};

export const fetchPaymentProviders = (currency: string) => {
  return getApiFactory().purchaseService.getPaymentProviders(currency);
};

export const fetchProductsAsset = async (asset: OptionalAssets) => {
  return getApiFactory().productService.getProgramsAndGroupsByAsset(asset);
};

export const fetchProgramsAndGroups = async () => {
  const {
    dataStore: { programStore, groupStore },
  } = getRoot();

  return getApiFactory()
    .programService.getProgramsAndGroups()
    .then((data) => {
      runInAction(() => {
        programStore.setPrograms(data.programs);
        groupStore.setGroups(data.groups);
      });
      return data;
    });
};

export const fetchAddOns = async (accountId: string) => {
  return getApiFactory().addOnService.getAddOnsForAccount(accountId);
};

// CART QUERIES
export const addToCart = (payload: AddItemToCartDto) => {
  return getApiFactory().cartService.addToCart(payload);
};

export const addItemByAccount = (payload: AddItemByAccountDto) => {
  return getApiFactory().cartService.addItemByAccount(payload);
};

export const removeFromCart = (payload: RemoveItemFromCartDto) => {
  return getApiFactory().cartService.removeFromCart(payload);
};

export const emptyCart = () => {
  return getApiFactory().cartService.emptyCart();
};

// CURRENCY QUERIES
export const fetchCurrencies = () => {
  return getApiFactory().purchaseService.getCurrencies();
};

export const changeCurrency = (payload: ChangeCurrencyForCartDto) => {
  return getApiFactory().cartService.changeCurrency(payload);
};

// USER QUERIES
export const fetchUserCredits = () => {
  return getApiFactory().creditsService.getCredits();
};

export const fetchUserCreditsHistory = () => {
  return getApiFactory().creditsService.getCreditsHistory();
};

export const fetchUserLogins = () => {
  return getApiFactory().accountService.getLogins();
};

export const fetchRiseId = () => {
  return getApiFactory().payoutService.getRiseId();
};

// PAYOUT QUERIES
export const fetchPayoutEligibility = (
  type: PayoutSource,
  sourceId: string,
) => {
  return getApiFactory().payoutService.getIsEligible(type, sourceId);
};

export const fetchPayouts = (
  type: PayoutSource,
  sourceId: number,
  paginateRequest: PaginationRequest,
) => {
  return getApiFactory().payoutService.getPayouts(
    type,
    sourceId,
    paginateRequest,
  );
};

export const fetchPaginatePayouts = (paginateRequest: PaginationRequest) => {
  return getApiFactory().payoutService.paginatePayouts(paginateRequest);
};

export const fetchPayoutTotalsAndCounts = (
  aggregationRequest: AggregationRequest,
) => {
  return getApiFactory().payoutService.getPayoutTotalsAndCounts(
    aggregationRequest,
  );
};

export const createPayoutRequest = (
  withdrawRequest: WithdrawPayload & {
    source: PayoutSource;
    sourceId: string;
  },
) => {
  return getApiFactory().payoutService.createPayoutRequest(withdrawRequest);
};

export const fetchAirwallexAuth = () => {
  return getApiFactory().payoutService.getAirwallexAuth();
};

export const createAirwallexBeneficiary = (
  airwallexForm: AirwallexBeneficiaryFormOptions,
) => {
  return getApiFactory().payoutService.createAirwallexBeneficiary(
    airwallexForm,
  );
};

export const fetchUserVerificationData = () => {
  return getApiFactory().userService.getUserVerificationData();
};

export const uploadProfilePicture = (file: FormData) => {
  return getApiFactory().userService.uploadProfilePicture(file);
};

export const deleteProfilePicture = () => {
  return getApiFactory().userService.deleteProfilePicture();
};

export const isValidUsername = (username: string) => {
  return getApiFactory().userService.isValidUsername(username);
};

// export const updateUser = async (
//   userDataToUpdate: Partial<User>,
// ): Promise<User> => {
//   const updatedUser =
//     await getApiFactory().userService.updateUser(userDataToUpdate);
//   if (!updatedUser) {
//     throw new Error('User update failed: No data returned');
//   }
//   return updatedUser;
// };

export const fetchKycAccessToken = () => {
  return getApiFactory().userService.getKycAccessToken();
};

export const registerAsAffiliate = () => {
  return getApiFactory().affiliationService.registerAsAffiliate();
};

export const createAffiliateRequest = (data: CreateAffiliateRequestType) => {
  return getApiFactory().affiliationService.createAffiliateRequest(data);
};

export const fetchUserAffiliationDashboard = () => {
  return getApiFactory().affiliationService.getUserAffiliationDashboard();
};

export const activateAffiliateSplit = () => {
  return getApiFactory().affiliationService.activateSplit();
};

export const revertAffiliateSplit = () => {
  return getApiFactory().affiliationService.revertSplit();
};

export const fetchAffiliateCommissions = async (params: PaginationRequest) => {
  return getApiFactory().affiliationService.getAffiliateCommissions(params);
};

export const fetchAffiliate = async (params: PaginationRequest) => {
  return getApiFactory().affiliationService.paginate<AffiliationCommission>(
    params,
  );
};

export const signUserContract = (params: {
  contractType: ContractType;
  contractData?: ContractData;
}) => {
  return getApiFactory().contractService.signUserContract(params);
};

export const fetchUserContracts = () => {
  return getApiFactory().contractService.getUserContracts();
};

export const fetchUserCoupons = () => {
  return getApiFactory().couponService.getUserCoupons();
};

export const fetchCouponMetrics = () => {
  return getApiFactory().couponService.getCouponMetrics();
};

export const addItemByCoupon = (payload: AddItemByCouponDto) => {
  return getApiFactory().cartService.addItemByCoupon(payload);
};

export const createUserCoupon = (params: { org: string; name: string }) => {
  return getApiFactory().couponService.createUserCoupon(params);
};

export const addCouponByCode = (payload: AddCouponByCodeDto) => {
  return getApiFactory().couponService.addCouponByCode(payload);
};

export const decryptPassword = ({
  login,
  tradingSystem,
}: {
  login: string;
  tradingSystem: TradingSystem;
}) => {
  return getApiFactory().accountService.decryptPassword(login, tradingSystem);
};

export const fetchAffiliateAvailableCommissions = () => {
  return getApiFactory().affiliationService.getAffiliateAvailableCommissions();
};

export const fetchAffiliateFaq = (
  application: Application,
): Promise<AffiliateFaq[]> => {
  return getApiFactory().affiliationService.getAffiliateFaq(application);
};

export const fetchAffiliatePayoutsTotal = (affiliateId: number) => {
  return getApiFactory().payoutService.getPayoutsTotal(
    PayoutSource.Affiliate,
    affiliateId,
  );
};
//FIXmE: !!!!!!!!
export const fetchAffiliateWithdrawalsCount = async (affiliateId: number) => {
  const paginateRequest: PaginationRequest = { page: 1, limit: 1 };
  const response = await getApiFactory().payoutService.getPayouts(
    PayoutSource.Affiliate,
    affiliateId,
    paginateRequest,
  );
  return response.total;
};

export const fetchAffiliateCouponCode = (affiliateId: string) => {
  return getApiFactory().affiliationService.getAffiliateCouponCode(affiliateId);
};

export const fetchCart = () => {
  return getApiFactory().cartService.getCart();
};

export const fetchCartDiscounts = () => {
  return getApiFactory().cartService.getCartDiscounts();
};

export const processAddOn = ({
  accountId,
  addOnId,
}: {
  accountId: string;
  addOnId: string;
}) => {
  return getApiFactory().accountService.processAddOn({ accountId, addOnId });
};

export const updateCartStep = (cartStep: CartStep) => {
  return getApiFactory().cartService.updateCartStep(cartStep);
};

export const updateDiscount = (payload: UpdateDiscountForCartDto) => {
  return getApiFactory().cartService.updateDiscount(payload);
};

export const removeDiscount = (payload: RemoveDiscountForCartDto) => {
  return getApiFactory().cartService.removeDiscount(payload);
};

export const updateTradingSystem = (payload: UpdateTradingSystemDto) => {
  return getApiFactory().cartService.updateTradingSystem(payload);
};

export const creditsOnCart = (payload: UseCreditsOnCartDto) => {
  return getApiFactory().cartService.creditsOnCart(payload);
};

export const submitCart = (payload: SubmitCartDto) => {
  return getApiFactory().cartService.submitCart(payload);
};

export const sendRiseInvitation = () => {
  return getApiFactory().payoutService.sendRiseInvitation();
};

export const fetchPublicProfile = (publicId?: string) => {
  return getApiFactory().userService.getPublicProfile(publicId);
};

export const approveUser = (approvalType: UserApproval) => {
  return getApiFactory().userService.userApproved(approvalType);
};

export const fetchAccountStatisticsData = async (
  tsAccountId: string,
): Promise<AccountStats> => {
  return getApiFactory().accountService.getStats(tsAccountId);
};

export const fetchContestAccountStatisticsData = async (
  tsAccountId: string,
): Promise<AccountStats> => {
  return getApiFactory().contestAccountService.getStats(tsAccountId);
};

export const fetchAccountBalanceData = async (
  tsAccountId: string,
): Promise<AccountBalance> => {
  return getApiFactory().contestAccountService.getBalance(tsAccountId);
};

export const fetchProgramProgressionData = async (
  programId: string,
): Promise<ProgressStep[]> => {
  return getApiFactory().programService.getProgramProgression(programId);
};

export const fetchAccountsForRunningContests =
  async (): Promise<AccountsForRunningContestsResponse> => {
    return getApiFactory().contestAccountService.getAccountsForRunningContests();
  };

export const fetchClosedContestWithWinners = async (
  page = 1,
): Promise<{ results: ClosedContestWithWinners[]; total: number }> => {
  const limit = 12;
  return getApiFactory().contestService.getClosedContestWithWinners(
    page,
    limit,
  );
};

export const fetchUserContestRanking = async (): Promise<
  UserContestRanking[]
> => {
  return getApiFactory().contestService.getUserContestRanking();
};

export const registerToContest = ({
  contestId,
  influencerCode,
}: {
  contestId: string;
  influencerCode?: string;
}) => {
  return getApiFactory().contestService.registerToContest(
    contestId,
    influencerCode,
  );
};

export const fetchContestInformation =
  async (): Promise<ContestInformation> => {
    const {
      dataStore: { contestStore },
    } = getRoot();

    const contestInformation =
      await getApiFactory().contestService.getContestInformation();

    runInAction(() => {
      contestStore.setContestInformation(contestInformation);
    });

    return contestInformation;
  };

export const submitTradeTalkFeedback = (feedback: {
  aiReview: 'like' | 'dislike' | null;
  feedbackText: string;
}) => {
  return getApiFactory().tradeTalkService.submitFeedback(feedback);
};

// FUTURES SYMBOLS QUERIES
export const fetchSupportedFuturesSymbols = async () => {
  return getApiFactory().futuresSymbolsService.getSupportedSymbols();
};

// COMMUNITY STATS QUERIES
export const fetchPayoutTicker = async () => {
  return getApiFactory().communityStatsService.getPayoutTicker();
};

export const fetchMarketStatsFilters = async () => {
  return getApiFactory().communityStatsService.getFilters();
};

export const fetchDurationAnalysis = async (
  symbol?: string,
  filters?: MarketStatsFilterParams,
) => {
  return getApiFactory().communityStatsService.getDurationAnalysis(
    symbol,
    filters,
  );
};

export const fetchRRRealization = async (
  symbol?: string,
  filters?: MarketStatsFilterParams,
) => {
  return getApiFactory().communityStatsService.getRRRealization(
    symbol,
    filters,
  );
};

export const fetchCoreMetrics = async (filters?: MarketStatsFilterParams) => {
  return getApiFactory().communityStatsService.getCoreMetrics(filters);
};

export const fetchDirectionalAnalytics = async (
  filters?: MarketStatsFilterParams,
  options?: { limit?: number; search?: string },
) => {
  return getApiFactory().communityStatsService.getDirectionalAnalytics(
    filters,
    options,
  );
};

export const fetchAgreementGauge = async (
  symbol?: string,
  filters?: MarketStatsFilterParams,
) => {
  return getApiFactory().communityStatsService.getAgreementGauge(
    symbol,
    filters,
  );
};

export const fetchOpenOrders = async (
  symbol: string,
  filters?: MarketStatsFilterParams,
) => {
  return getApiFactory().communityStatsService.getOpenOrders(symbol, filters);
};

export const fetchPositionsMap = async (
  symbol: string,
  filters?: MarketStatsFilterParams,
) => {
  return getApiFactory().communityStatsService.getPositionsMap(symbol, filters);
};

export const fetchAssetDetail = async (
  symbol: string,
  filters?: MarketStatsFilterParams,
) => {
  return getApiFactory().communityStatsService.getAssetDetail(symbol, filters);
};

export const fetchPinnedAssets = async () => {
  return getApiFactory().communityStatsService.getPinnedAssets();
};

export const pinAsset = async (symbol: string) => {
  return getApiFactory().communityStatsService.pinAsset(symbol);
};

export const unpinAsset = async (symbol: string) => {
  return getApiFactory().communityStatsService.unpinAsset(symbol);
};

// POSITION QUERIES
export const fetchPositionTrades = (
  tsAccountId: string,
  positionId: string,
) => {
  return getApiFactory().positionService.getTrades(tsAccountId, positionId);
};

// RELEASE QUERIES
export const fetchReleases = (params?: { since?: string }) => {
  return getApiFactory().releaseService.getReleases(params);
};

export const fetchReleaseById = (id: string) => {
  return getApiFactory().releaseService.getReleaseById(id);
};

export const recordReleaseViews = (releaseIds: string[]) => {
  return getApiFactory().releaseService.recordViews(releaseIds);
};
